/**
 * useWorkspaceRoles — fetches the current user's roles for the active workspace.
 *
 * Calls GET /api/v1/tenant-user-dashboard/me/workspace-roles.
 * Automatically re-fetches when the active workspace changes.
 * Falls back to empty roles (no restrictions) when the endpoint is unreachable
 * or the BFF returns no role info (e.g. stub token in dev).
 */

import { useCallback, useEffect, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { DASHBOARD_PAGE_KEYS } from "@/lib/dashboardPages";
import { tenantApiFetch, tenantDashboardPath } from "@/lib/tenantApi";

const PAGE_KEY_SET = new Set(DASHBOARD_PAGE_KEYS);

function normRoleToken(r) {
  return String(r || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

/** Normalized JWT workspace role → canonical page_key, if any (align with BFF JwtPageScopeMapper). */
function roleToPageKey(role) {
  const n = normRoleToken(role);
  if (!n) return null;
  const aliases = {
    inbox: "chat",
    email: "email_service",
    widget: "widget_generator",
  };
  const mapped = aliases[n] ?? n;
  return PAGE_KEY_SET.has(mapped) ? mapped : null;
}

const ROLES_PATH = tenantDashboardPath("/me/workspace-roles");

/**
 * @returns {{
 *   roles: string[],
 *   isAdmin: boolean,
 *   canMutate: boolean,
 *   loading: boolean,
 * }}
 */
export function useWorkspaceRoles() {
  const { activeWorkspaceId } = useWorkspace();
  const [roles, setRoles] = useState(/** @type {string[]} */ ([]));
  const [loading, setLoading] = useState(false);

  const fetchRoles = useCallback(async () => {
    if (!activeWorkspaceId) {
      setRoles([]);
      return;
    }
    setLoading(true);
    try {
      const data = await tenantApiFetch(ROLES_PATH);
      setRoles(Array.isArray(data?.roles) ? data.roles : []);
    } catch {
      // Fallback: no restrictions (allows dev/stub token environments to function)
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    void fetchRoles();
  }, [fetchRoles]);

  const isAdmin = roles.includes("admin");
  const hasPageScope = roles.some((r) => roleToPageKey(r) !== null);
  const canMutate = roles.length === 0 || isAdmin || hasPageScope;

  return { roles, isAdmin, canMutate, loading };
}
